/**
 * PostHog Analytics Helper
 * Provides easy-to-use functions for tracking events in your application
 * Note: PostHog events are only fired in production (IS_PROD=True)
 */

let posthogLib = null;

class PostHogHelper {
  constructor() {
    this.isInitialized = false;
    this.posthog = null;
    this.isProd = process.env.IS_PROD === "True" || process.env.IS_PROD === "true";
    this.init();
  }

  async init() {
    // Only initialize PostHog in production environment
    if (!this.isProd) {
      console.log("PostHog disabled in non-production environment");
      this.setNoopPosthog();
      return;
    }

    // Try to import posthog-js dynamically if window.posthog is undefined
    if (typeof window !== "undefined") {
      if (window.posthog) {
        // If already loaded on window, use it
        this.posthog = window.posthog;
        this.isInitialized = true;
        console.log("PostHog is available in the browser (window.posthog)");
      } else {
        try {
          // Dynamically import posthog-js if not present on window
          if (!posthogLib) {
            posthogLib = (await import("posthog-js")).default;
          }
          // Initialize PostHog if not already initialized
          if (!window.posthog) {
            // You must set your POSTHOG_API_KEY and POSTHOG_API_HOST here
            const POSTHOG_API_KEY =
              "phc_xZKHpsqHMh9a3WWxqQoNId3bzXYFY5SFfcpIROrDsSe";
            const POSTHOG_API_HOST = "https://us.i.posthog.com";
            if (POSTHOG_API_KEY) {
              posthogLib.init(POSTHOG_API_KEY, { api_host: POSTHOG_API_HOST });
              window.posthog = posthogLib;
              this.posthog = window.posthog;
              this.isInitialized = true;
              console.log("PostHog initialized via posthog-js import");
            } else {
              console.warn(
                "PostHog API key is missing. Analytics will not be initialized."
              );
              this.setNoopPosthog();
            }
          } else {
            this.posthog = window.posthog;
            this.isInitialized = true;
            console.log(
              "PostHog is available in the browser (window.posthog after import)"
            );
          }
        } catch (err) {
          console.error("Failed to load posthog-js:", err);
          this.setNoopPosthog();
        }
      }
    } else {
      // Fallback for server-side rendering
      this.setNoopPosthog();
    }
  }

  setNoopPosthog() {
    this.posthog = {
      capture: () => {},
      identify: () => {},
      set: () => {},
      people: {
        set: () => {},
        set_once: () => {},
      },
    };
    this.isInitialized = false;
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {object} properties - Additional properties for the event
   */
  track(eventName, properties = {}) {
    if (!this.isProd) {
      return;
    }
    if (
      this.isInitialized &&
      this.posthog &&
      typeof this.posthog.capture === "function"
    ) {
      this.posthog.capture(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
        url: typeof window !== "undefined" ? window.location.href : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });
    }
  }

  /**
   * Identify a user
   * @param {string} userId - Unique identifier for the user
   * @param {object} userProperties - User properties to set
   */
  identify(userId, userProperties = {}) {
    if (!this.isProd) {
      return;
    }
    if (
      this.isInitialized &&
      this.posthog &&
      typeof this.posthog.identify === "function"
    ) {
      this.posthog.identify(userId, userProperties);
    }
  }

  /**
   * Set user properties
   * @param {object} properties - Properties to set for the current user
   */
  setUserProperties(properties) {
    if (!this.isProd) {
      return;
    }
    if (
      this.isInitialized &&
      this.posthog &&
      this.posthog.people &&
      typeof this.posthog.people.set === "function"
    ) {
      this.posthog.people.set(properties);
    }
  }

  /**
   * Set user properties once (won't override existing values)
   * @param {object} properties - Properties to set once for the current user
   */
  setUserPropertiesOnce(properties) {
    if (!this.isProd) {
      return;
    }
    if (
      this.isInitialized &&
      this.posthog &&
      this.posthog.people &&
      typeof this.posthog.people.set_once === "function"
    ) {
      this.posthog.people.set_once(properties);
    }
  }

  /**
   * Track page view
   * @param {string} pageName - Name of the page
   * @param {object} properties - Additional properties
   */
  trackPageView(pageName, properties = {}) {
    this.track("page_view", {
      page_name: pageName,
      page_url: typeof window !== "undefined" ? window.location.href : "",
      ...properties,
    });
  }

  /**
   * Track user registration
   * @param {string} userId - User ID
   * @param {object} userData - User data
   */
  trackRegistration(userId, userData = {}) {
    this.identify(userId, userData);
    this.track("user_registered", {
      user_id: userId,
      ...userData,
    });
  }

  /**
   * Track user login
   * @param {string} userId - User ID
   * @param {object} userData - User data
   */
  trackLogin(userId, userData = {}) {
    this.identify(userId, userData);
    this.track("user_logged_in", {
      user_id: userId,
      ...userData,
    });
  }

  /**
   * Track article view
   * @param {string} articleId - Article ID
   * @param {string} articleTitle - Article title
   * @param {object} properties - Additional properties
   */
  trackArticleView(articleId, articleTitle, properties = {}) {
    this.track("article_viewed", {
      article_id: articleId,
      article_title: articleTitle,
      ...properties,
    });
  }

  /**
   * Track subscription
   * @param {string} planType - Type of subscription plan
   * @param {number} amount - Amount paid
   * @param {object} properties - Additional properties
   */
  trackSubscription(planType, amount, properties = {}) {
    this.track("subscription_started", {
      plan_type: planType,
      amount: amount,
      currency: "INR",
      ...properties,
    });
  }

  /**
   * Track search
   * @param {string} searchTerm - Search term
   * @param {object} properties - Additional properties
   */
  trackSearch(searchTerm, properties = {}) {
    this.track("search_performed", {
      search_term: searchTerm,
      ...properties,
    });
  }

  /**
   * Track newsletter subscription
   * @param {string} email - Email address
   * @param {object} properties - Additional properties
   */
  trackNewsletterSubscription(email, properties = {}) {
    this.track("newsletter_subscribed", {
      email: email,
      ...properties,
    });
  }

  /**
   * Track social share
   * @param {string} platform - Social platform (facebook, twitter, linkedin, etc.)
   * @param {string} contentId - ID of the shared content
   * @param {object} properties - Additional properties
   */
  trackSocialShare(platform, contentId, properties = {}) {
    this.track("social_share", {
      platform: platform,
      content_id: contentId,
      ...properties,
    });
  }

  /**
   * Track button click
   * @param {string} buttonName - Name/ID of the button
   * @param {string} pageName - Page where button was clicked
   * @param {object} properties - Additional properties
   */
  trackButtonClick(buttonName, pageName, properties = {}) {
    this.track("button_clicked", {
      button_name: buttonName,
      page_name: pageName,
      ...properties,
    });
  }

  /**
   * Track form submission
   * @param {string} formName - Name of the form
   * @param {object} properties - Additional properties
   */
  trackFormSubmission(formName, properties = {}) {
    this.track("form_submitted", {
      form_name: formName,
      ...properties,
    });
  }

  /**
   * Track error
   * @param {string} errorMessage - Error message
   * @param {string} errorType - Type of error
   * @param {object} properties - Additional properties
   */
  trackError(errorMessage, errorType = "general", properties = {}) {
    this.track("error_occurred", {
      error_message: errorMessage,
      error_type: errorType,
      ...properties,
    });
  }

  /**
   * Get PostHog instance
   * @returns {object} PostHog instance
   */
  getInstance() {
    return this.posthog;
  }

  /**
   * Check if PostHog is initialized
   * @returns {boolean} True if initialized
   */
  isReady() {
    return this.isInitialized;
  }
}

// Create a singleton instance
const posthogHelper = new PostHogHelper();

export default posthogHelper;
